import { Truck, Sparkles, ShieldCheck } from 'lucide-react';

interface TrustBadgesProps {
  language: 'en' | 'ar';
}

/**
 * Trust-signal strip shown above the H1 on the city Index page.
 * Three short value props with icons — bot-renderable real text so Google sees the
 * "Same-Day Delivery" and "Fresh Flowers Guarantee" phrasing in the prerendered HTML.
 *
 * Mobile: 3-column compact grid, icon-on-top, centered. Floward-style.
 * Desktop (≥ sm): same 3-column grid but horizontal — icon left, text right.
 */
const TrustBadges = ({ language }: TrustBadgesProps) => {
  const badges = [
    {
      icon: Truck,
      en: { title: 'Same-Day Delivery', sub: 'Order before 5 PM' },
      ar: { title: 'توصيل في نفس اليوم', sub: 'اطلب قبل 5 العصر' },
    },
    {
      icon: Sparkles,
      en: { title: 'Fresh Flowers Guarantee', sub: 'Hand-arranged daily' },
      ar: { title: 'ضمان ورد فريش', sub: 'بنرتّبه كل يوم باليد' },
    },
    {
      icon: ShieldCheck,
      en: { title: 'Secure Card Payment', sub: 'Visa, Mastercard, Apple Pay' },
      ar: { title: 'دفع آمن بالكارت', sub: 'فيزا، ماستركارد، آبل باي' },
    },
  ];

  const isRtl = language === 'ar';

  return (
    <section
      aria-label={isRtl ? 'مميزات بوكيه' : 'Bouqueh advantages'}
      className="py-4 sm:py-6 border-y bg-muted/30"
    >
      <div className="container mx-auto px-3 sm:px-4">
        <ul
          className="grid grid-cols-3 gap-2 sm:gap-4 max-w-5xl mx-auto"
          dir={isRtl ? 'rtl' : 'ltr'}
        >
          {badges.map(({ icon: Icon, en, ar }, i) => {
            const copy = isRtl ? ar : en;
            return (
              <li
                key={i}
                className={[
                  // Mobile: stack vertically inside each cell, icon on top, centered text.
                  // Desktop (sm+): switch to row, icon left of text.
                  'flex flex-col items-center text-center gap-1.5',
                  'sm:flex-row sm:items-center sm:text-start sm:gap-3',
                  isRtl ? 'sm:flex-row-reverse sm:text-end' : '',
                ].join(' ')}
              >
                <span
                  className={[
                    'flex-shrink-0 rounded-full bg-primary/10 flex items-center justify-center',
                    // Smaller circle on mobile, original size on desktop.
                    'w-9 h-9 sm:w-10 sm:h-10',
                  ].join(' ')}
                >
                  <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-primary" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <div className="font-semibold text-[11px] leading-tight sm:text-sm md:text-base text-foreground">
                    {copy.title}
                  </div>
                  <div className="text-[10px] leading-tight sm:text-xs text-muted-foreground mt-0.5 sm:mt-0">
                    {copy.sub}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export default TrustBadges;
